import { useEffect, useState } from 'react';
import { SidebarSection } from './SidebarSection';
import { NAVIGATION_SECTIONS } from '../../config/navigationConfig';

interface SidebarNavigationProps {
  currentPage: string;
  onPageChange: (page: string) => void;
  isCollapsed: boolean;
  user: any;
}

const STORAGE_KEY = 'sidebar_sections_state';

export function SidebarNavigation({
  currentPage,
  onPageChange,
  isCollapsed,
  user,
}: SidebarNavigationProps) {
  // Load initial state from localStorage or defaults
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch {}
    
    // Default: use defaultOpen from config
    return NAVIGATION_SECTIONS.reduce((acc, section) => ({
      ...acc,
      [section.id]: section.defaultOpen ?? false,
    }), {});
  });

  // Auto-expand section containing current page
  useEffect(() => {
    const currentSection = NAVIGATION_SECTIONS.find(section =>
      section.items.some(item => item.page === currentPage)
    );
    
    if (currentSection) {
      setOpenSections(prev => {
        // Only update if not already open to prevent unnecessary re-renders
        if (!prev[currentSection.id]) {
          return {
            ...prev,
            [currentSection.id]: true,
          };
        }
        return prev;
      });
    }
  }, [currentPage]);

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(openSections));
    } catch {}
  }, [openSections]);

  const toggleSection = (sectionId: string) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // Filter sections based on permissions
  const visibleSections = NAVIGATION_SECTIONS.map(section => ({
    ...section,
    items: section.items.filter(item =>
      !item.permission || item.permission(user)
    ),
  })).filter(section => section.items.length > 0);

  return (
    <nav className="space-y-1.5" role="navigation" aria-label="Main navigation">
      {visibleSections.map((section) => (
        <SidebarSection
          key={section.id}
          section={section}
          currentPage={currentPage}
          onPageChange={onPageChange}
          isCollapsed={isCollapsed}
          isOpen={openSections[section.id] ?? false}
          onToggle={() => toggleSection(section.id)}
        />
      ))}
    </nav>
  );
}

